import React, {useContext, useEffect, useRef, useState} from "react";
import BaseInstallment from "./BaseInstallment";

import {initialAllowedCalculationParameters} from "./initialAllowedParameters";
import {goVehisService} from "../../../utils/axios";
import ContractLengthChooser from "./ContractLengthChooser";
import FeeSlider from "./FeeSlider";
import {CustomerTypeContext, isCustomerCompany} from "../../../context/customer-type-context";
import MathUtils from "../../../utils/MathUtils";
import InstallmentSubmitBox from "./InstallmentSubmitBox";
import VehicleInsuranceCheckbox from "./VehicleInsuranceCheckbox";
import VehicleGapCheckbox from "./VehicleGapCheckbox";
import DrivePlusCheckbox from "./DrivePlusCheckbox";
import CalculationSummary from "./CalculationSummary";
import BuyoutValueSlider from "./BuyoutValueSlider";
import AppToaster from "../../../components/AppToaster";
import contractLengthBuyoutMap from "./contractLengthBuyoutMap";
import analytics from "../../../utils/analytics";
import * as axios from "axios";
import ContactDialog from "../../../components/ContactDialog/ContactDialog";
import {Intent, Overlay, Spinner} from "@blueprintjs/core";

function InstallmentCalculator(props) {

    const MAX_PRICE_NET = 300000;
    const customerContext = useContext(CustomerTypeContext);

    let contractLengthRef = useRef(null);
    let initialFeeRef = useRef(null);
    let buyoutValueRef = useRef(null);
    const source = axios.CancelToken.source();

    let vehiclePrice = isCustomerCompany(customerContext) ? props.offer.minimumPriceNet : props.offer.minimumPriceGross;

    const [calculationResponse, setCalculationResponse] = useState({
        "calculationResult": {
            "vehicleValueNet": props.offer.minimumPriceNet,
            "vehicleValueGross": props.offer.minimumPriceGross,
            "buyoutValueNet": 0,
            "buyoutValueGross": 0,
            "initialFeeValueNet": 0,
            "initialFeeValueGross": 0,
            "installmentNet": props.offer.installmentNet,
            "installmentGross": props.offer.installmentGross,
            "numberOfInstallments": 60,
            "ocAcInsuranceNet": 0,
            "ocAcInsuranceGross": 0,
            "gapInsuranceNet": 0,
            "gapInsuranceGross": 0,
            "installmentWithInsuranceNet": props.offer.installmentNet,
            "installmentWithInsuranceGross": props.offer.installmentGross
        },
        "insuranceFees": {
            "ocAcInsuranceFor12MonthsNet": 0,
            "ocAcInsuranceFor12MonthsGross": 0,
            "ocAcInsuranceForAllMonthsNet": 0,
            "ocAcInsuranceForAllMonthsGross": 0,
            "gapInsuranceNet": 0,
            "gapInsuranceGross": 0
        }
    });

    const defaultContractLength = props.initialCalculation ? props.initialCalculation.contractLength : 60;

    const [calculationParameters, setCalculationParameters] = useState(
        {
            vehicleValueNet: props.offer.minimumPriceNet,
            vehicleType: props.offer.vehicleType,
            initialFeePercentage: props.initialCalculation ? MathUtils.percentageOfValue(vehiclePrice, props.initialCalculation.initialFeeValueNet) : 10,
            buyoutValuePercentage: props.initialCalculation ? MathUtils.percentageOfValue(vehiclePrice, props.initialCalculation.buyoutValueNet) : 25,
            contractLength: defaultContractLength,
            customerType: customerContext[0],
            manufactureYear: props.offer.manufactureYear,
            decimalPrecision: 0
        }
    );

    const [defaultBuyoutPercentage, setDefaultBuyoutPercentage] = useState(calculationParameters.buyoutValuePercentage);

    const isSpinnerEnabled = props.spinnerEnabled ? props.spinnerEnabled : false;
    const [isOpenSpinner, setIsOpenSpinner] = useState(isSpinnerEnabled);

    useEffect(() => {
        setCalculationParameters(prevState => ({...prevState, vehicleValueNet: props.offer.minimumPriceNet}))
    }, [props.offer.minimumPriceNet]);

    const customerType = customerContext[0];

    useEffect(() => {
        setCalculationParameters(prevState => prevState.customerType === customerType
            ? prevState
            : {...prevState, customerType});
    }, [customerType]);

    useEffect(() => {
        console.log(props.offer)
    }, [props.offer])

    useEffect(() => {
        setIsOpenSpinner(true);

        goVehisService.post(props.calculatorUrl, {...calculationParameters, ...(props.extraCalculationParams || {})}, {
            cancelToken: source.token
        }).then(response => {
            let calculationResult = response.data;
            if (calculationResult !== undefined) {
                setCalculationResponse(calculationResult);
                setIsOpenSpinner(false);
            }
        }).catch(error => {
            if (axios.isCancel(error)) {
                console.log("request cancelled: " + error.message);
            } else {
                console.log("another error happened: " + error.message);
            }
        });

        return () => {
            source.cancel();
        };
    }, [calculationParameters]);

    const [allowedCalculationParameters, setAllowedCalculationParameters] = useState(initialAllowedCalculationParameters);

    useEffect(() => {
        setIsOpenSpinner(true);
        goVehisService(props.allowedCalculationParametersUrl).then(response => {
            let allowedParameters = response.data;
            if (allowedParameters !== undefined) {
                setAllowedCalculationParameters(allowedParameters);
                setIsOpenSpinner(false);
            }
        });
    }, [props.offer.minimumPriceNet]);

    const contractLengthChangeHandler = (newContractLength) => {
        setCalculationParameters(prevState => {
            if (prevState.contractLength !== newContractLength) {
                analytics.sendEvent({
                    category: 'OL - zmiana miesiecy',
                    action: 'Klikniecie',
                    label: 'Kliknieto w ' + newContractLength,
                    value: 1
                });
            }
            return {
                ...prevState,
                contractLength: newContractLength,
                buyoutValuePercentage: contractLengthBuyoutMap[newContractLength]
            };
        });
        setDefaultBuyoutPercentage(contractLengthBuyoutMap[newContractLength]);
    };

    const initialFeeChangeHandler = (initialFee) => {
        let initialFeePercentage = MathUtils.percentageOfValue(vehiclePrice, initialFee);
        setCalculationParameters(prevState => ({...prevState, initialFeePercentage: initialFeePercentage}));
    };

    const buyoutValueChangeHandler = (buyoutValue) => {
        let buyoutValuePercentage = MathUtils.percentageOfValue(vehiclePrice, buyoutValue);
        setCalculationParameters(prevState => ({...prevState, buyoutValuePercentage: buyoutValuePercentage}));
    };

    const goToContractLengthHandler = () => {
        analytics.sendEvent({category: 'OL', action: 'Klikniecie', label: 'Zmieniono OL', value: 1});
        window.scrollTo({
            behavior: 'smooth',
            top: contractLengthRef.current.getBoundingClientRect().top + window.scrollY - 50
        });
    };

    const goToInitialFeeHandler = () => {
        analytics.sendEvent({category: 'OW', action: 'Klikniecie', label: 'Zmieniono OW', value: 1});
        window.scrollTo({
            behavior: 'smooth',
            top: initialFeeRef.current.getBoundingClientRect().top + window.scrollY - 50
        });
    }

    const [insuranceChecked, setInsuranceChecked] = useState(false);
    const [insuranceData, setInsuranceData] = useState(null);

    const [gapChecked, setGapChecked] = useState(false);
    const [gapData, setGapData] = useState(null);
    const gapFinancingType = isCustomerCompany(customerContext) ? "LEASING_BUSINESS" : "LEASING_CONSUMER";

    const [showBuyoutValue, setShowBuyoutValue] = useState(!!props.buyoutAlwaysVisible);
    const showBuyoutValueHandler = () => {
        if (!props.buyoutAlwaysVisible) {
            setShowBuyoutValue(prevState => !prevState);
        }
        analytics.sendEvent({category: 'Wykup', action: 'Klikniecie', label: 'Zmieniono wykup', value: 1});
        window.scrollTo({behavior: 'smooth', top: buyoutValueRef.current.getBoundingClientRect().top + window.scrollY - 50});
    }

    const [isOpenContactDialog, setIsOpenContactDialog] = useState(false);

    const drivePlusInstallmentNet = calculationResponse.additionalComponents
        ? calculationResponse.additionalComponents.drivePlusInstallmentNet : null;
    const drivePlusInstallmentGross = calculationResponse.additionalComponents
        ? calculationResponse.additionalComponents.drivePlusInstallmentGross : null;
    const drivePlusInstallment = isCustomerCompany(customerContext) ? drivePlusInstallmentNet : drivePlusInstallmentGross;

    const reservationClickHandler = () => {
        analytics.sendEvent({category: 'Zarezerwuj', action: 'Klikniecie', label: 'Kliknieto w Zarezerwuj', value: 1});
        let calculationOffer = {
            vehicle: props.offer,
            financingCompany: props.financingCompany,
            calculation: {
                vehicleValueNet: calculationResponse.calculationResult.vehicleValueNet,
                vehicleValueGross: calculationResponse.calculationResult.vehicleValueGross,
                buyoutValueNet: calculationResponse.calculationResult.buyoutValueNet,
                buyoutValueGross: calculationResponse.calculationResult.buyoutValueGross,
                initialFeeValueNet: calculationResponse.calculationResult.initialFeeValueNet,
                initialFeeValueGross: calculationResponse.calculationResult.initialFeeValueGross,
                installmentNet: calculationResponse.calculationResult.installmentNet,
                installmentGross: calculationResponse.calculationResult.installmentGross,
                contractLength: calculationParameters.contractLength,
                installmentWithoutInsurance: calculationResponse.calculationResult.installmentNet,
                insuranceLength: 0,
                ocAcInsuranceInstallment: 0,
                gapInstallment: 0,
                ocAcInsuranceChecked: insuranceChecked,
                ocAcInsuranceData: insuranceChecked ? insuranceData : null,
                gapChecked: gapChecked,
                gapData: gapChecked ? gapData : null,
                drivePlusInstallmentNet: drivePlusInstallmentNet,
                drivePlusInstallmentGross: drivePlusInstallmentGross
            }
        };
        props.reservationStrategy(true, calculationOffer);
    }

    const contactClickHandler = () => {
        setIsOpenContactDialog(true);
        analytics.sendEvent({category: 'Zapytaj', action: 'Klikniecie', label: 'Kliknieto w Zapytaj', value: 1});
    }

    return (
        <div className="calc__wrapper">
            <div className="calc__content">
                <BaseInstallment
                    installment={{
                        net: calculationResponse.calculationResult.installmentNet,
                        gross: calculationResponse.calculationResult.installmentGross
                    }}
                    offer={props.offer}
                    label="Rata leasingu"
                    onBannerClick={contactClickHandler}
                />
                <span ref={contractLengthRef}/>
                <form action="">
                    <ContractLengthChooser
                        items={allowedCalculationParameters.contractLengthMonths}
                        defaultLength={calculationParameters.contractLength}
                        onChange={contractLengthChangeHandler}
                        label="Okres leasingu (ilość miesięcznych rat)"
                    />
                    <div ref={initialFeeRef} className="calc__form-item">
                        <FeeSlider
                            infoCode="oplata-wstepna"
                            onChange={initialFeeChangeHandler}
                            defaultValue={MathUtils.valueOfPercentage(vehiclePrice, calculationParameters.initialFeePercentage)}
                            value={MathUtils.valueOfPercentage(vehiclePrice, calculationParameters.initialFeePercentage)}
                            brutto={!isCustomerCompany(customerContext)}
                            min={isCustomerCompany(customerContext) ?
                                allowedCalculationParameters.initialFeeValues.minimumInitialFeeNet : allowedCalculationParameters.initialFeeValues.minimumInitialFeeGross}
                            max={isCustomerCompany(customerContext) ?
                                allowedCalculationParameters.initialFeeValues.maximumInitialFeeNet - 500 : allowedCalculationParameters.initialFeeValues.maximumInitialFeeGross - 500}
                            calculationResult={calculationResponse.calculationResult}
                            vehiclePrice={vehiclePrice}
                        />
                    </div>
                    {props.buyoutAlwaysVisible && (
                        <div ref={buyoutValueRef} className="calc__form-item">
                            <BuyoutValueSlider
                                infoCode="wykup"
                                onChange={buyoutValueChangeHandler}
                                defaultValue={MathUtils.valueOfPercentage(vehiclePrice, calculationParameters.buyoutValuePercentage)}
                                value={MathUtils.valueOfPercentage(vehiclePrice, defaultBuyoutPercentage)}
                                brutto={!isCustomerCompany(customerContext)}
                                buyoutValues={allowedCalculationParameters.buyoutValues}
                                contractLength={calculationParameters.contractLength}
                                vehiclePrice={vehiclePrice}
                            />
                        </div>
                    )}
                    <DrivePlusCheckbox installment={drivePlusInstallment}/>
                    <VehicleInsuranceCheckbox
                        vehicleId={props.offer.id}
                        checked={insuranceChecked}
                        onChange={() => setInsuranceChecked(prev => !prev)}
                        onDataLoaded={setInsuranceData}
                    />
                    <VehicleGapCheckbox
                        vehicleId={props.offer.id}
                        financingType={gapFinancingType}
                        financingMonths={calculationParameters.contractLength}
                        checked={gapChecked}
                        onChange={() => setGapChecked(prev => !prev)}
                        onDataLoaded={setGapData}
                    />
                    <p className="calc__insurance-disclaimer">*Składka ma charakter poglądowy i może ulec zmianie.</p>
                    {!props.buyoutAlwaysVisible && (
                        <>
                            <span ref={buyoutValueRef}/>
                            <div id="formWykup" className={(showBuyoutValue ? "" : "hidden ") + "calc__form-item"}>
                                <BuyoutValueSlider
                                    infoCode="wykup"
                                    onChange={buyoutValueChangeHandler}
                                    defaultValue={MathUtils.valueOfPercentage(vehiclePrice, calculationParameters.buyoutValuePercentage)}
                                    value={MathUtils.valueOfPercentage(vehiclePrice, defaultBuyoutPercentage)}
                                    brutto={!isCustomerCompany(customerContext)}
                                    buyoutValues={allowedCalculationParameters.buyoutValues}
                                    contractLength={calculationParameters.contractLength}
                                    vehiclePrice={vehiclePrice}
                                />
                            </div>
                        </>
                    )}
                    {isSpinnerEnabled &&
                        <Overlay isOpen={isOpenSpinner} usePortal={false}>
                            <Spinner intent={Intent.PRIMARY} className="calculator-spinner"/>
                        </Overlay>
                    }
                </form>
            </div>
            <div className="calc__summary">
                <CalculationSummary
                    isNew={props.offer.isNew}
                    allInsuranceVisible={false}
                    isIndividualInsurance={false}
                    isGwarancjaZwrotuActive={false}
                    calculationParameters={calculationParameters}
                    calculationResult={calculationResponse.calculationResult}
                    offer={props.offer}
                    changeContractLength={goToContractLengthHandler}
                    changeInitialFee={goToInitialFeeHandler}
                    changeBuyoutValue={showBuyoutValueHandler}
                    insuranceChecked={insuranceChecked}
                    insuranceData={insuranceData}
                    gapChecked={gapChecked}
                    gapData={gapData}
                    drivePlusInstallment={drivePlusInstallment}
                />
                <InstallmentSubmitBox
                    installment={{
                        net: calculationResponse.calculationResult.installmentWithInsuranceNet,
                        gross: calculationResponse.calculationResult.installmentWithInsuranceGross
                    }}
                    onReservationClick={reservationClickHandler}
                    onSendMessageClick={contactClickHandler}
                />
            </div>
            <ContactDialog isOpen={isOpenContactDialog}
                           onClose={() => setIsOpenContactDialog(false)}
                           additionalMessage={"Dotyczy oferty: " + window.location.href}
            />
        </div>
    );
}

export default InstallmentCalculator;
